// =============================================================================
// PPIHistorico — quem participou do PPI, e onde cada um parou
// =============================================================================
// No espírito do histórico da Transição de Cargo, mas o PPI tem 4 portões — então
// a linha mostra o CAMINHO INTEIRO: inscrição → triagem → prova → chão → resultado.
//
// A NOTA DA PROVA é calculada no servidor NA LEITURA (nunca foi armazenada): se o
// RH anular uma questão hoje, o histórico já mostra a nota nova. Não é cache velho.
//
// A lista de APROVADOS sai impressa (PDF com espaço de assinatura, para levar à
// sala) ou em xlsx (para trabalhar os dados) — e é EXATAMENTE a mesma consulta que
// alimenta a tabela, então o que se imprime é o que se vê.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Printer, FileSpreadsheet, RefreshCcw, Users, FileText } from 'lucide-react';
import { CORES, BadgeStatus, Botao, CartaoKpi, estilos, usePaginacao, RodapePaginacao } from './ppiUI';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

// As listas que fazem sentido imprimir. Cada uma leva as colunas dela — imprimir
// a nota da prova numa lista de convocação seria vazar informação. A de reprovados
// na triagem (2026-09-24) é informativa, sai sem coluna de assinatura.
const ETAPAS = [
    { chave: 'triagem', rotulo: 'Convocados para a prova (aprovados na triagem)' },
    { chave: 'triagem_reprovados', rotulo: 'Reprovados na triagem' },
    { chave: 'prova', rotulo: 'Aprovados na prova' },
    { chave: 'final', rotulo: 'Aprovados no PPI (resultado final)' },
];

const TOM_ETAPA = (e) => e?.startsWith('PPI Aprovado') ? 'ativo'
    : e?.startsWith('PPI Reprovado') || e?.startsWith('Reprovado') ? 'reprovado'
        : e?.startsWith('Aguardando') ? 'emProcesso' : 'neutro';

const ITENS_POR_PAGINA = 15;

// Valores distintos de um campo, em ordem alfabética — alimenta os selects de filtro.
const opcoes = (linhas, campo) =>
    [...new Set(linhas.map((l) => l[campo]).filter(Boolean))]
        .sort((a, b) => String(a).localeCompare(String(b), 'pt-BR'));

const fmtNota = (v) => v == null ? '—' : Number(v).toFixed(2).replace('.', ',');
const fmtPct = (v) => v == null ? '—' : `${Number(v).toFixed(1).replace('.', ',')}%`;
const fmtData = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

function PPIHistorico() {
    const [processos, setProcessos] = useState([]);
    const [idProcesso, setIdProcesso] = useState('');
    const [linhas, setLinhas] = useState([]);
    const [filtroEtapa, setFiltroEtapa] = useState('');   // filtro visual (client-side)
    const [filtros, setFiltros] = useState({ loja: '', vaga: '' });  // idem — peneira a lista carregada
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');
    const [etapaImpressao, setEtapaImpressao] = useState('final');

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch('/api/treinamento/ppi/processos', { headers: authHeaders() });
                if (r.ok) setProcessos(await r.json());
            } catch (_) { /* silencioso */ }
        })();
    }, []);

    const carregar = useCallback(async () => {
        setCarregando(true); setErro('');
        try {
            const q = idProcesso ? `?processo=${idProcesso}` : '';
            const r = await fetch(`/api/treinamento/ppi/historico${q}`, { headers: authHeaders() });
            if (!r.ok) throw new Error('Erro ao carregar o histórico.');
            setLinhas(await r.json());
        } catch (e) { setErro(e.message); }
        finally { setCarregando(false); }
    }, [idProcesso]);

    useEffect(() => { carregar(); }, [carregar]);

    // Opções dos selects saem da própria lista carregada.
    const lojas = useMemo(() => opcoes(linhas, 'LOJA_ATUAL'), [linhas]);
    const vagas = useMemo(() => opcoes(linhas, 'VAGA'), [linhas]);

    // Recorte de ESCOPO (loja/vaga). É sobre ele que os KPIs e as contagens dos
    // chips são calculados — a etapa é um drill-down por dentro desse escopo.
    const noEscopo = useMemo(() => linhas.filter((l) =>
        (!filtros.loja || l.LOJA_ATUAL === filtros.loja) &&
        (!filtros.vaga || l.VAGA === filtros.vaga)
    ), [linhas, filtros]);

    const filtradas = useMemo(
        () => filtroEtapa ? noEscopo.filter((l) => l.ETAPA_FINAL === filtroEtapa) : noEscopo,
        [noEscopo, filtroEtapa]);

    const etapasPresentes = useMemo(
        () => [...new Set(noEscopo.map((l) => l.ETAPA_FINAL))].sort(),
        [noEscopo]);

    // Se a etapa escolhida sumiu do escopo (filtrou uma loja onde ninguém está
    // naquela etapa), volta para "Todos" — senão a tela fica vazia sem explicação.
    useEffect(() => {
        if (filtroEtapa && !etapasPresentes.includes(filtroEtapa)) setFiltroEtapa('');
    }, [filtroEtapa, etapasPresentes]);

    const { paginados, pagina, setPagina, totalPaginas, inicio, total } =
        usePaginacao(filtradas, ITENS_POR_PAGINA);

    // Qualquer troca de recorte recomeça na 1ª página.
    useEffect(() => { setPagina(1); }, [idProcesso, filtros, filtroEtapa, setPagina]);

    // A impressão é um GET autenticado → baixa como blob e abre/salva.
    // (Um <a href> simples não passaria o token do portal.)
    const imprimir = async (formato) => {
        setErro('');
        try {
            const params = new URLSearchParams({ etapa: etapaImpressao, formato });
            if (idProcesso) params.set('processo', idProcesso);
            // A lista impressa segue os MESMOS filtros da tela — filtrar por uma
            // loja e imprimir a rede inteira seria uma armadilha.
            if (filtros.loja) params.set('loja', filtros.loja);
            if (filtros.vaga) params.set('cargo', filtros.vaga);
            const r = await fetch(`/api/treinamento/ppi/historico/aprovados?${params}`, { headers: authHeaders() });
            if (!r.ok) throw new Error((await r.json()).message || 'Erro ao gerar a lista.');
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            if (formato === 'pdf') {
                window.open(url, '_blank');           // abre para conferir e imprimir
            } else {
                const a = document.createElement('a');
                a.href = url;
                const rotulo = etapaImpressao === 'triagem_reprovados' ? 'reprovados_triagem' : `aprovados_${etapaImpressao}`;
                a.download = `PPI_${rotulo}.xlsx`;
                a.click();
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { setErro(e.message); }
    };

    // Abre um PDF autenticado numa aba nova. Mesma mecânica do `imprimir`: um
    // <a href> não passaria o token do portal, então baixa-se como blob.
    const abrirPdf = async (url, erroPadrao) => {
        setErro('');
        try {
            const r = await fetch(url, { headers: authHeaders() });
            if (!r.ok) throw new Error((await r.json()).message || erroPadrao);
            const objeto = URL.createObjectURL(await r.blob());
            window.open(objeto, '_blank');
            setTimeout(() => URL.revokeObjectURL(objeto), 60000);
        } catch (e) { setErro(e.message); }
    };

    // A ficha de UMA pessoa: triagem, acertos por seção da prova, LIVE e as decisões.
    const resumoDoCandidato = (l) =>
        abrirPdf(`/api/treinamento/ppi/candidato/${l.ID}/resumo/pdf`, 'Erro ao gerar o resumo do candidato.');

    // ⚠️ O consolidado é POR PROCESSO — exige um processo escolhido. Com "Todos os
    // processos" não existe consolidado: misturar edições num relatório por trilha
    // somaria gente de PPIs diferentes na mesma linha.
    const consolidadoDoProcesso = () =>
        abrirPdf(`/api/treinamento/ppi/processos/${idProcesso}/consolidado/pdf`, 'Erro ao gerar o consolidado.');

    // KPIs seguem o escopo (loja/vaga), não o chip de etapa — o chip é justamente
    // o detalhamento desses números.
    const inscritos = noEscopo.length;
    const aprovadosProva = noEscopo.filter((l) => l.PROVA_RESULTADO === 'Aprovado').length;
    const concluidos = noEscopo.filter((l) => l.CHAO_RESULTADO === 'Aprovado').length;

    const alterarFiltro = (campo, valor) => setFiltros((f) => ({ ...f, [campo]: valor }));
    const temFiltro = !!(filtros.loja || filtros.vaga);

    return (
        <div>
            <div style={estilos.barraFiltros}>
                <div>
                    <label style={estilos.rotuloFiltro}>Processo</label>
                    <select style={estilos.input} value={idProcesso} onChange={(e) => setIdProcesso(e.target.value)}>
                        <option value="">Todos os processos</option>
                        {processos.map((p) => <option key={p.ID} value={p.ID}>{p.NOME_PROCESSO}</option>)}
                    </select>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Loja</label>
                    <select style={estilos.input} value={filtros.loja} onChange={(e) => alterarFiltro('loja', e.target.value)}>
                        <option value="">Todas as lojas</option>
                        {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Vaga</label>
                    <select style={estilos.input} value={filtros.vaga} onChange={(e) => alterarFiltro('vaga', e.target.value)}>
                        <option value="">Todas as vagas</option>
                        {vagas.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Botao variant="secundario" icone={RefreshCcw} onClick={carregar}>Atualizar</Botao>
                    {temFiltro && (
                        <button type="button" onClick={() => setFiltros({ loja: '', vaga: '' })}
                            style={{
                                background: 'none', border: 'none', color: CORES.primaria,
                                cursor: 'pointer', fontSize: '13px', fontWeight: 700, padding: 0,
                            }}>
                            limpar
                        </button>
                    )}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px', marginBottom: '22px' }}>
                <CartaoKpi titulo="Inscritos" valor={inscritos} icone={Users} cor={CORES.primaria} />
                <CartaoKpi titulo="Aprovados na prova" valor={aprovadosProva} cor="#b45309" />
                <CartaoKpi titulo="PPI concluído (aprovados)" valor={concluidos} cor="#059669" />
            </div>

            {/* IMPRESSÃO da lista de aprovados */}
            <div style={{ border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '16px', marginBottom: '22px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 800, color: CORES.texto, margin: '0 0 4px 0' }}>Imprimir listas do PPI</h4>
                <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '0 0 12px 0' }}>
                    {etapaImpressao === 'triagem_reprovados'
                        ? <>O PDF sai com o cabeçalho do processo — é a lista de quem <strong>não passou na triagem</strong>, informativa (sem assinatura).</>
                        : <>O PDF sai com o cabeçalho do processo e <strong>espaço para assinatura</strong> — é para levar impresso.</>}
                    {temFiltro && (
                        <> A lista sai com os filtros da tela: <strong>
                            {[filtros.loja, filtros.vaga].filter(Boolean).join(' · ')}
                        </strong>.</>
                    )}
                </p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ minWidth: '320px', flex: 1 }}>
                        <label style={estilos.rotuloFiltro}>Qual lista</label>
                        <select style={estilos.input} value={etapaImpressao} onChange={(e) => setEtapaImpressao(e.target.value)}>
                            {ETAPAS.map((e) => <option key={e.chave} value={e.chave}>{e.rotulo}</option>)}
                        </select>
                    </div>
                    <Botao icone={Printer} onClick={() => imprimir('pdf')}>PDF para imprimir</Botao>
                    <Botao variant="secundario" icone={FileSpreadsheet} onClick={() => imprimir('xlsx')}>Excel</Botao>
                </div>
            </div>

            {/* CONSOLIDADO do processo, separado por trilha (2026-08-04).
                Bloco separado do de cima de propósito: aquele imprime uma LISTA de
                nomes para levar à sala; este é o retrato da edição inteira, para
                fechar o ciclo. Juntá-los no mesmo cartão faria escolher "qual lista"
                parecer que também vale para o consolidado. */}
            <div style={{ border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '16px', marginBottom: '22px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 800, color: CORES.texto, margin: '0 0 4px 0' }}>Consolidado do processo</h4>
                <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '0 0 12px 0' }}>
                    Todos os candidatos da edição, <strong>separados por trilha</strong>, com triagem, nota da prova e LIVE.
                    {!idProcesso && <> Escolha um processo no filtro acima — o consolidado é de <strong>uma</strong> edição.</>}
                </p>
                <Botao icone={FileText} onClick={consolidadoDoProcesso} disabled={!idProcesso}>
                    Consolidado por trilha (PDF)
                </Botao>
            </div>

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}
            {carregando && <p style={{ color: CORES.textoFraco }}>Carregando…</p>}

            {/* Filtro visual por etapa */}
            {!!etapasPresentes.length && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                    <Chip ativo={!filtroEtapa} onClick={() => setFiltroEtapa('')}>Todos ({noEscopo.length})</Chip>
                    {etapasPresentes.map((e) => (
                        <Chip key={e} ativo={filtroEtapa === e} onClick={() => setFiltroEtapa(e)}>
                            {e} ({noEscopo.filter((l) => l.ETAPA_FINAL === e).length})
                        </Chip>
                    ))}
                </div>
            )}

            {!carregando && !linhas.length && (
                <p style={{ color: CORES.textoFraco }}>Nenhum candidato inscrito ainda.</p>
            )}
            {!carregando && !!linhas.length && !filtradas.length && (
                <p style={{ color: CORES.textoFraco }}>
                    Nenhum candidato com esses filtros — os {linhas.length} da lista ficaram de fora.
                </p>
            )}

            {!!filtradas.length && (
                <div style={estilos.tabelaWrap}>
                    <table style={estilos.tabela}>
                        <thead><tr>
                            <th style={estilos.th}>Candidato</th>
                            <th style={estilos.th}>Loja</th>
                            <th style={estilos.th}>Vaga</th>
                            <th style={{ ...estilos.th, textAlign: 'center' }}>Faltas</th>
                            <th style={{ ...estilos.th, textAlign: 'center' }}>Atestados</th>
                            <th style={estilos.th}>Triagem</th>
                            <th style={{ ...estilos.th, textAlign: 'center' }}>Nota da prova</th>
                            <th style={estilos.th}>Prova</th>
                            <th style={{ ...estilos.th, textAlign: 'center' }}>LIVE</th>
                            <th style={estilos.th}>Situação</th>
                            <th style={{ ...estilos.th, textAlign: 'right' }}></th>
                        </tr></thead>
                        <tbody>
                            {paginados.map((l) => (
                                <tr key={l.ID}>
                                    <td style={estilos.td}>
                                        <strong>{l.NOME}</strong>
                                        <div style={{ fontSize: '12px', color: CORES.textoFraco }}>
                                            {l.CARGO_ATUAL} · inscrito em {fmtData(l.DATA_INSCRICAO)}
                                        </div>
                                    </td>
                                    <td style={estilos.td}>{l.LOJA_ATUAL}</td>
                                    <td style={estilos.td}>{l.VAGA}</td>
                                    <td style={{ ...estilos.td, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                                        {l.FALTAS_AJUSTE ?? l.FALTAS_APURADAS ?? '—'}
                                    </td>
                                    <td style={{ ...estilos.td, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                                        {l.ATESTADOS_AJUSTE ?? l.ATESTADOS_APURADOS ?? '—'}
                                    </td>
                                    <td style={estilos.td}><Resultado v={l.TRIAGEM_RESULTADO} /></td>
                                    <td style={{ ...estilos.td, textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                                        {fmtNota(l.NOTA)}
                                    </td>
                                    <td style={estilos.td}><Resultado v={l.PROVA_RESULTADO} /></td>
                                    <td style={{ ...estilos.td, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                                        {fmtPct(l.CHAO_PERCENTUAL)}
                                    </td>
                                    <td style={estilos.td}>
                                        <BadgeStatus tom={TOM_ETAPA(l.ETAPA_FINAL)}>{l.ETAPA_FINAL}</BadgeStatus>
                                    </td>
                                    {/* A ficha da pessoa — é aqui que se "seleciona o candidato"
                                        para gerar o PDF, na linha dele, sem uma tela à parte. */}
                                    <td style={{ ...estilos.td, textAlign: 'right' }}>
                                        <button type="button" onClick={() => resumoDoCandidato(l)}
                                            title={`Resumo do processo de ${l.NOME} em PDF`}
                                            style={{
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                color: CORES.primaria, fontSize: '12px', fontWeight: 700,
                                                display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px',
                                            }}>
                                            <FileText size={13} /> resumo
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <RodapePaginacao
                        pagina={pagina} totalPaginas={totalPaginas} inicio={inicio}
                        quantidade={paginados.length} total={total}
                        onPagina={setPagina} rotulo="candidato(s)" />
                </div>
            )}
        </div>
    );
}

const Resultado = ({ v }) => {
    if (!v) return <span style={{ color: CORES.textoFraco }}>—</span>;
    return <BadgeStatus tom={v === 'Aprovado' ? 'ativo' : 'reprovado'}>{v}</BadgeStatus>;
};

const Chip = ({ ativo, onClick, children }) => (
    <button onClick={onClick} style={{
        padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
        border: `1px solid ${ativo ? CORES.primaria : CORES.borda}`,
        backgroundColor: ativo ? CORES.primaria : '#fff',
        color: ativo ? '#fff' : CORES.textoSec,
    }}>{children}</button>
);

export default PPIHistorico;
